import React, { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useBuyerNotifications } from '../../hooks/useBuyerNotifications';
import { isActiveSession } from '../../utils/verification';

function Declaration({
  className = 'pr-5 text-white text-2xl font-bold',
  panelPositionClass = 'mt-2 origin-top-right',
}) {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    refresh,
    markAsRead,
    markAllAsRead,
    formatRelativeTime,
  } = useBuyerNotifications({
    // An unverified session is capped to its own user record, so polling
    // notifications only produces a stream of 403s (#145).
    enabled: isActiveSession(user),
  });

  if (!isActiveSession(user)) {
    return null;
  }

  const handleNotificationClick = async (notification, close) => {
    if (!notification.is_read && notification.timestamp) {
      try {
        await markAsRead([notification.timestamp]);
      } catch {
        /* refresh will surface errors */
      }
    }

    close();

    if (notification.redirect_url) {
      const path = notification.redirect_url.startsWith('/')
        ? notification.redirect_url
        : `/${notification.redirect_url}`;
      navigate(path);
    }
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={className}
            aria-label="Notifications"
            onClick={() => refresh()}
          >
            <span className="relative inline-flex">
              <svg
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                viewBox="0 0 24 24"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-6 h-6 drop-shadow-xl"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </span>
          </Popover.Button>

          <Transition
            as={Fragment}
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Popover.Panel
              className={`absolute right-0 z-50 w-80 sm:w-96 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${panelPositionClass}`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllAsRead()}
                    className="text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {isLoading && notifications.length === 0 && (
                <p className="px-4 py-6 text-sm text-center text-gray-500">Loading…</p>
              )}

              {error && (
                <div className="px-4 py-4 text-center">
                  <p className="text-sm text-red-600 mb-2">{error}</p>
                  <button
                    type="button"
                    onClick={() => refresh()}
                    className="text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!error && !isLoading && notifications.length === 0 && (
                <p className="px-4 py-6 text-sm text-center text-gray-500">No notifications</p>
              )}

              {notifications.length > 0 && (
                <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <li key={notification.timestamp}>
                      <button
                        type="button"
                        onClick={() => handleNotificationClick(notification, close)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          !notification.is_read ? 'bg-brand-50/60' : ''
                        }`}
                      >
                        <p className="text-sm text-gray-900">{notification.msg}</p>
                        <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                          <span>{notification.model || 'System'}</span>
                          <span>{formatRelativeTime(notification.timestamp)}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default Declaration;
